import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { FormWrapper } from 'ksnm-common-ui/lib/Form/FormWrapper';
import { Notification } from 'ksnm-common-ui/lib/Notification';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { usePermission } from '@src/utils/hooks/usePermission';
import { reportSettingEditStore } from '@stores/dictionaryStores/reportSettings/reportSettingEditStore';
import { reportSettingsStore } from '@stores/dictionaryStores/reportSettings/reportSettingsStore';
import { reportVersionsStore } from '@stores/dictionaryStores/reportVersion/reportVersionsStore';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { FormWithActions } from '../../FormWithActions';
import { FunctionalFields } from './FunctionalFields';
import { MainFields } from './MainFields';
import { validate } from './validateSchema';

const ReportSettingEditComponent = () => {
  const [tab, setTab] = useState('main');

  const { code } = useParams<{ code: string }>();
  const { push } = useHistory();
  const { setNew, fetch, setEditMode, isLoading, reportSetting, save, isNew, isEditMode } = reportSettingEditStore;
  const { organizationCode, reportSettingsLevel } = reportSettingsStore;
  const { reportVersions } = reportVersionsStore;

  const permission = usePermission('CmReportSettings', reportSettingEditStore);

  useEffect(() => {
    if (code !== NEW_ENTITY_ID) {
      fetch(code);
    } else {
      setNew(true);
    }
    return () => {
      setNew(false);
      setEditMode(false);
    };
  }, [fetch, code, setNew, setEditMode]);

  useEffect(() => {
    if (!organizationCode) push(RoutePath.DICTIONARY_REPORT_SETTINGS);
  }, [organizationCode, push]);

  const initialValues = useMemo(() => {
    if (!reportSetting) {
      return { isReportStructureVisible: true, isReportPublishVisible: true, organizationCode };
    }

    const group = reportVersions?.find((item) => item.code === reportSetting?.reportVersionCode)?.reportGroupCode;

    const levelId = reportSettingsLevel?.find((item) => {
      if (item.level === 'level4') {
        return item.level === reportSetting?.url && (group === 'D01' ? item.groupLevel === 'D01' : !item.groupLevel);
      }

      return item.level === reportSetting?.url;
    })?.id;

    return isLoading
      ? {}
      : toJS({
          ...reportSetting,
          id: reportSetting?.reportVersionCode,
          organizationCode,
          signTcc: reportSetting?.signTcc === '1',
          signInfoSystem: reportSetting?.signInfoSystem === '1',
          autoForm: reportSetting?.autoForm === '1',
          arbitraryReport: reportSetting?.arbitraryReport === '1',
          autoDecodeDetail: reportSetting?.autoDecodeDetail === '1',
          autoCheckVersion: reportSetting?.autoCheckVersion === '1',
          dataFiltering: reportSetting?.dataFiltering === '1',
          dataSorting: reportSetting?.dataSorting === '1',
          dataGrouping: reportSetting?.dataGrouping === '1',
          totalCalc: reportSetting?.totalCalc === '1',
          dataExport: reportSetting?.dataExport === '1',
          dataPrint: reportSetting?.dataPrint === '1',
          fulltextSearchRus: reportSetting?.fulltextSearchRus === '1',
          levelId,
        }); // id нужен для проверки на уникальность кода
  }, [reportSetting, reportVersions, reportSettingsLevel, isLoading, organizationCode]);

  const handleSubmit = useCallback(
    (values) => {
      if ((isNew && !permission.CREATE) || (!isNew && !permission.UPDATE)) {
        Notification({
          message: `У вас нет прав для выполнения операции ${isNew ? 'создания' : 'обновления'} формы`,
        });
        return {};
      }

      const validation = validate(values);

      if (validation) {
        return validation;
      }

      (async () => {
        const level = reportSettingsLevel?.find((item) => item.id === values.levelId)?.level;

        const reportSettings = await save({
          ...values,
          url: level,
          signTcc: values.signTcc ? '1' : '0',
          signInfoSystem: values.signInfoSystem ? '1' : '0',
          autoForm: values.autoForm ? '1' : '0',
          arbitraryReport: values.arbitraryReport ? '1' : '0',
          autoDecodeDetail: values.autoDecodeDetail ? '1' : '0',
          autoCheckVersion: values.autoCheckVersion ? '1' : '0',
          dataFiltering: values.dataFiltering ? '1' : '0',
          dataSorting: values.dataSorting ? '1' : '0',
          dataGrouping: values.dataGrouping ? '1' : '0',
          totalCalc: values.totalCalc ? '1' : '0',
          dataExport: values.dataExport ? '1' : '0',
          dataPrint: values.dataPrint ? '1' : '0',
          fulltextSearchRus: values.fulltextSearchRus ? '1' : '0',
        });

        if (isNew && reportSettings?.reportVersionCode) {
          push(`${RoutePath.DICTIONARY_REPORT_SETTINGS}/${reportSettings.reportVersionCode}`);
        }
      })();

      return undefined;
    },
    [isNew, permission.CREATE, permission.UPDATE, reportSettingsLevel, save, push],
  );

  const goToReportSettings = useCallback(() => push(RoutePath.DICTIONARY_REPORT_SETTINGS), [push]);

  const tabs = useMemo(
    () => ({
      items: [
        { name: 'Основные', value: 'main' },
        { name: 'Функциональные возможности отчета', value: 'funct' },
      ],
      active: tab,
      onChange: setTab,
    }),
    [tab],
  );

  return (
    <DesignLayout title="Справочник: Настройка отчета" scrollEnable requiredHelp={isEditMode}>
      <FormWrapper initialValues={initialValues} onSubmit={handleSubmit} layout="vertical" compact>
        <FormWithActions store={reportSettingEditStore} goBackLink={goToReportSettings} tabs={tabs}>
          <>
            {tab === 'main' && <MainFields />}
            {tab === 'funct' && <FunctionalFields />}
          </>
        </FormWithActions>
      </FormWrapper>
    </DesignLayout>
  );
};

export const ReportSettingEdit = observer(ReportSettingEditComponent);
